/**
 * The horn: synthesised, so there is no sound file to load.
 *
 * A car horn is two electric horns sounding together a third or so apart,
 * each a buzzing, square-ish tone, rasped by the diaphragm and boxed in by the
 * trumpet. Two square waves a major third apart, through a low-pass and a
 * little soft clipping, are near enough. A motorbike has one smaller horn,
 * higher and thinner. An e-scooter has no horn at all but a piezo beeper
 * on the bars: one thin, high tone.
 *
 * It plays through the game's mixer (`audio/system.ts`): the player's own
 * horn on the effects bus, other drivers' toots on the world's, both under
 * the HUD's volume and mute.
 */

import type { SoundSystem } from './audio/system.js';

export type HornVoice = 'car' | 'moto' | 'scooter';

const VOICES: Record<HornVoice, number[]> = {
  car: [370, 466],
  moto: [520],
  scooter: [2093],
};
/** How loud each is, and how much of its top the trumpet (or the beeper's little case) lets through, Hz. */
const LEVEL: Record<HornVoice, number> = { car: 0.22, moto: 0.22, scooter: 0.11 };
const CUTOFF: Record<HornVoice, number> = { car: 1900, moto: 2600, scooter: 5200 };

export class Horn {
  private sounding: { gain: GainNode; oscillators: OscillatorNode[] } | undefined;

  constructor(private readonly sound: SoundSystem) {}

  /** Sound the horn, or stop it. Cheap to call every frame. */
  set(on: boolean, voice: HornVoice = 'car'): void {
    if (on && this.sounding === undefined) this.start(voice);
    else if (!on && this.sounding !== undefined) this.stop();
  }

  /**
   * A short toot from a car `distance` metres off: another driver's horn,
   * pitched a little differently from the player's, fainter with distance
   * and silent past 150 m.
   */
  toot(distance: number): void {
    const level = 0.16 * Math.max(0, 1 - distance / 150) ** 2;
    const ctx = this.sound.context, out = this.sound.input('world');
    if (level < 0.004 || ctx === undefined || out === undefined || !this.sound.live) return;
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(level, now + 0.015);
    gain.gain.setValueAtTime(level, now + 0.32);
    gain.gain.linearRampToValueAtTime(0, now + 0.4);
    const tone = ctx.createBiquadFilter();
    tone.type = 'lowpass';
    // Further off, duller.
    tone.frequency.value = 1900 - Math.min(1100, distance * 8);
    tone.Q.value = 2.5;
    const rasp = ctx.createWaveShaper();
    rasp.curve = softClip();
    rasp.connect(tone).connect(gain).connect(out);
    const pitch = 0.88 + Math.random() * 0.2;
    for (const frequency of VOICES.car) {
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = frequency * pitch;
      const part = ctx.createGain();
      part.gain.value = 0.5;
      osc.connect(part).connect(rasp);
      osc.start(now);
      osc.stop(now + 0.42);
    }
  }

  private start(voice: HornVoice): void {
    const ctx = this.sound.context, out = this.sound.input('effects');
    if (ctx === undefined || out === undefined || !this.sound.live) return;
    const now = ctx.currentTime;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(LEVEL[voice], now + 0.015);
    const tone = ctx.createBiquadFilter();
    tone.type = 'lowpass';
    tone.frequency.value = CUTOFF[voice];
    tone.Q.value = 2.5;
    const rasp = ctx.createWaveShaper();
    rasp.curve = softClip();
    rasp.connect(tone).connect(gain).connect(out);

    const oscillators = VOICES[voice].map((frequency) => {
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = frequency;
      // A horn never quite holds its pitch.
      osc.detune.value = (Math.random() - 0.5) * 12;
      const level = ctx.createGain();
      level.gain.value = 0.5;
      osc.connect(level).connect(rasp);
      osc.start(now);
      return osc;
    });
    this.sounding = { gain, oscillators };
  }

  private stop(): void {
    const ctx = this.sound.context!;
    const { gain, oscillators } = this.sounding!;
    this.sounding = undefined;
    const now = ctx.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(0, now + 0.06);
    for (const osc of oscillators) osc.stop(now + 0.08);
  }
}

let clip: Float32Array<ArrayBuffer> | undefined;

/** A gentle tanh curve: rounds the square waves' corners into a buzz. */
function softClip(): Float32Array<ArrayBuffer> {
  if (clip === undefined) {
    clip = new Float32Array(256);
    for (let i = 0; i < clip.length; i++) {
      const x = (i / (clip.length - 1)) * 2 - 1;
      clip[i] = Math.tanh(x * 2.2) / Math.tanh(2.2);
    }
  }
  return clip;
}
